export type InputFrame = {
  steer: number;
  gaitDelta: number;
  lookBack: boolean;
  phoneToggle: boolean;
  /** Flashlight auto/off toggle pressed this frame. */
  flashlightToggle: boolean;
  lookX: number;
  lookY: number;
  restart: boolean;
  confirm: boolean;
  pause: boolean;
  /** Quick-reply choice pressed this frame (0-based), or -1. */
  reply: number;
};

type TouchElements = {
  stick: HTMLElement;
  knob: HTMLElement;
  back: HTMLElement;
  phone: HTMLElement;
  light: HTMLElement;
};

/**
 * Collects keyboard, mouse and touch input into one intent frame per tick.
 * The mouse does not turn the camera (only touch drags do); right-click looks back.
 * Edge-triggered actions (gait changes, phone toggle) are counted and consumed.
 */
export class InputController {
  private readonly keys = new Set<string>();
  private gaitDelta = 0;
  private phoneToggle = false;
  private flashlightToggle = false;
  private restart = false;
  private confirm = false;
  private pause = false;
  private reply = -1;
  private lookX = 0;
  private lookY = 0;
  private rightMouse = false;
  private touchBack = false;
  private stickId: number | null = null;
  private stickX = 0;
  private stickGaitLatch = 0;
  private lookTouchId: number | null = null;
  private lookTouchLast = { x: 0, y: 0 };
  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Tab') e.preventDefault();
    if (e.repeat) {
      this.keys.add(e.code);
      return;
    }
    this.keys.add(e.code);
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.gaitDelta += 1;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.gaitDelta -= 1;
        break;
      case 'Tab':
      case 'KeyM':
        this.phoneToggle = true;
        break;
      case 'KeyF':
        this.flashlightToggle = true;
        break;
      case 'KeyR':
        this.restart = true;
        break;
      case 'Enter':
      case 'Space':
        this.confirm = true;
        break;
      case 'KeyP':
      case 'Escape':
        this.pause = true;
        break;
      case 'Digit1':
      case 'Numpad1':
        this.reply = 0;
        break;
      case 'Digit2':
      case 'Numpad2':
        this.reply = 1;
        break;
      default:
        break;
    }
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.rightMouse = false;
  };

  private readonly onMouseDown = (e: MouseEvent) => {
    if (e.button === 2) this.rightMouse = true;
  };

  private readonly onMouseUp = (e: MouseEvent) => {
    if (e.button === 2) this.rightMouse = false;
  };

  private readonly onContextMenu = (e: Event) => e.preventDefault();

  private readonly onStickDown = (e: PointerEvent) => {
    e.preventDefault();
    this.stickId = e.pointerId;
    try {
      this.touch.stick.setPointerCapture(e.pointerId);
    } catch {
      // synthetic events
    }
    this.updateStick(e.clientX, e.clientY);
  };

  private readonly onStickMove = (e: PointerEvent) => {
    if (e.pointerId !== this.stickId) return;
    e.preventDefault();
    this.updateStick(e.clientX, e.clientY);
  };

  private readonly onStickUp = (e: PointerEvent) => {
    if (e.pointerId !== this.stickId) return;
    this.stickId = null;
    this.stickX = 0;
    this.stickGaitLatch = 0;
    this.touch.knob.style.transform = 'translate(-50%, -50%)';
  };

  private readonly onCanvasPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    if (this.lookTouchId !== null) return;
    this.lookTouchId = e.pointerId;
    this.lookTouchLast = { x: e.clientX, y: e.clientY };
  };

  private readonly onCanvasPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== this.lookTouchId) return;
    this.lookX += (e.clientX - this.lookTouchLast.x) * 1.4;
    this.lookY += (e.clientY - this.lookTouchLast.y) * 1.4;
    this.lookTouchLast = { x: e.clientX, y: e.clientY };
  };

  private readonly onCanvasPointerUp = (e: PointerEvent) => {
    if (e.pointerId === this.lookTouchId) this.lookTouchId = null;
  };

  private readonly hold = (setter: (v: boolean) => void) => ({
    down: (e: PointerEvent) => {
      e.preventDefault();
      setter(true);
    },
    up: (e: PointerEvent) => {
      e.preventDefault();
      setter(false);
    },
  });

  private readonly backHold = this.hold((v) => (this.touchBack = v));
  private readonly onPhoneTap = (e: PointerEvent) => {
    e.preventDefault();
    this.phoneToggle = true;
  };

  private readonly onLightTap = (e: PointerEvent) => {
    e.preventDefault();
    this.flashlightToggle = true;
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly touch: TouchElements,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', this.onContextMenu);
    canvas.addEventListener('pointerdown', this.onCanvasPointerDown);
    canvas.addEventListener('pointermove', this.onCanvasPointerMove);
    canvas.addEventListener('pointerup', this.onCanvasPointerUp);
    canvas.addEventListener('pointercancel', this.onCanvasPointerUp);
    touch.stick.addEventListener('pointerdown', this.onStickDown);
    touch.stick.addEventListener('pointermove', this.onStickMove);
    touch.stick.addEventListener('pointerup', this.onStickUp);
    touch.stick.addEventListener('pointercancel', this.onStickUp);
    for (const [el, h] of [[touch.back, this.backHold]] as const) {
      el.addEventListener('pointerdown', h.down);
      el.addEventListener('pointerup', h.up);
      el.addEventListener('pointercancel', h.up);
      el.addEventListener('pointerleave', h.up);
    }
    touch.phone.addEventListener('pointerdown', this.onPhoneTap);
    touch.light.addEventListener('pointerdown', this.onLightTap);
  }

  /** Read and consume this frame's intents. */
  read(): InputFrame {
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft');
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight');
    let steer = (right ? 1 : 0) - (left ? 1 : 0);
    if (this.stickId !== null) steer = Math.abs(this.stickX) > 0.15 ? this.stickX : steer;

    const frame: InputFrame = {
      steer,
      gaitDelta: this.gaitDelta,
      lookBack: this.keys.has('KeyQ') || this.rightMouse || this.touchBack,
      phoneToggle: this.phoneToggle,
      flashlightToggle: this.flashlightToggle,
      lookX: this.lookX,
      lookY: this.lookY,
      restart: this.restart,
      confirm: this.confirm,
      pause: this.pause,
      reply: this.reply,
    };
    this.gaitDelta = 0;
    this.phoneToggle = false;
    this.flashlightToggle = false;
    this.restart = false;
    this.confirm = false;
    this.pause = false;
    this.reply = -1;
    this.lookX = 0;
    this.lookY = 0;
    return frame;
  }

  private updateStick(clientX: number, clientY: number): void {
    const rect = this.touch.stick.getBoundingClientRect();
    const radius = rect.width * 0.42;
    let x = (clientX - (rect.left + rect.width / 2)) / radius;
    let y = (clientY - (rect.top + rect.height / 2)) / radius;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    this.stickX = x;
    // Pushing up/down past the threshold steps the walking pace once per push.
    const latch = y < -0.65 ? -1 : y > 0.65 ? 1 : 0;
    if (latch !== 0 && latch !== this.stickGaitLatch) this.gaitDelta += latch < 0 ? 1 : -1;
    this.stickGaitLatch = latch;
    this.touch.knob.style.transform = `translate(calc(-50% + ${x * 38}px), calc(-50% + ${y * 38}px))`;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);
    this.canvas.removeEventListener('pointerdown', this.onCanvasPointerDown);
    this.canvas.removeEventListener('pointermove', this.onCanvasPointerMove);
    this.canvas.removeEventListener('pointerup', this.onCanvasPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onCanvasPointerUp);
    this.touch.stick.removeEventListener('pointerdown', this.onStickDown);
    this.touch.stick.removeEventListener('pointermove', this.onStickMove);
    this.touch.stick.removeEventListener('pointerup', this.onStickUp);
    this.touch.stick.removeEventListener('pointercancel', this.onStickUp);
    for (const [el, h] of [[this.touch.back, this.backHold]] as const) {
      el.removeEventListener('pointerdown', h.down);
      el.removeEventListener('pointerup', h.up);
      el.removeEventListener('pointercancel', h.up);
      el.removeEventListener('pointerleave', h.up);
    }
    this.touch.phone.removeEventListener('pointerdown', this.onPhoneTap);
    this.touch.light.removeEventListener('pointerdown', this.onLightTap);
  }
}
